// src/features/sessions/hooks/useNowAvailableHelpliners.ts
import { useQuery } from '@tanstack/react-query';

import { portalHelplineService } from '@/features/portal-helpline/services/portalHelplineService';

export interface AvailableHelpliner {
  practitioner: {
    id: string;
    firstName: string;
    lastName: string;
  };
  blockId: string;
  assignmentId: string;
  locationIds: string[];
}

/** Canonical key for the resolved available-now list. Shared with the
 *  helpline.availability-changed listener so its invalidation matches. */
export const availableNowKey = ['helpline', 'available-now'] as const;

/**
 * Practitioners on shift and callable for a helpline session right now.
 * One call to `GET /portal-helpline/available-now`; the server applies the
 * full eligibility rule. Kept fresh by the `helpline.availability-changed`
 * listener and the realtime reconnect self-heal — no polling.
 *
 * `businessAssociateId` is accepted for call-site compatibility but unused:
 * the backend resolves the caller's location + BA from their token.
 */
export function useNowAvailableHelpliners(_businessAssociateId: string) {
  return useQuery({
    queryKey: availableNowKey,
    queryFn: async (): Promise<AvailableHelpliner[]> => {
      const rows = await portalHelplineService.listAvailableNow();
      return rows.map((r) => ({
        practitioner: { id: r.practitionerId, firstName: r.firstName, lastName: r.lastName },
        blockId: r.blockId,
        assignmentId: r.assignmentId,
        locationIds: r.locationIds,
      }));
    },
  });
}
